import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    IconButton,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Paper,
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Timer as TimerIcon,
    TrendingUp as TrendingUpIcon,
    Refresh as RefreshIcon,
    FilterList as FilterListIcon,
    ExpandMore as ExpandMoreIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import DateRangePicker from '../../../components/DateRangePicker';
import {
    fetchQueries,
    fetchStatistics,
    deleteQuery,
    setPage,
    setPageSize,
    setSortColumn,
    setSortDirection,
    setFilterDeviceId,
    setFilterUnitName,
    setFilterQueryType,
    setFilterStatus,
    setFilterSecurityLevel,
    setFilterStartDate,
    setFilterEndDate,
    clearFilters,
    selectQueries,
    selectQueriesLoading,
    selectQueriesTotal,
    selectQueriesPage,
    selectQueriesPageSize,
    selectQueriesSortColumn,
    selectQueriesSortDirection,
    selectStatistics,
    selectStatisticsLoading,
    selectFilterDeviceId,
    selectFilterUnitName,
    selectFilterQueryType,
    selectFilterStatus,
    selectFilterSecurityLevel,
    selectFilterStartDate,
    selectFilterEndDate,
} from './slice';

export default function Tab3VerificationQueries() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Selectors
    const queries = useSelector(selectQueries);
    const loading = useSelector(selectQueriesLoading);
    const total = useSelector(selectQueriesTotal);
    const page = useSelector(selectQueriesPage);
    const pageSize = useSelector(selectQueriesPageSize);
    const sortColumn = useSelector(selectQueriesSortColumn);
    const sortDirection = useSelector(selectQueriesSortDirection);
    const statistics = useSelector(selectStatistics);
    const statisticsLoading = useSelector(selectStatisticsLoading);

    // Filters
    const filterDeviceId = useSelector(selectFilterDeviceId);
    const filterUnitName = useSelector(selectFilterUnitName);
    const filterQueryType = useSelector(selectFilterQueryType);
    const filterStatus = useSelector(selectFilterStatus);
    const filterSecurityLevel = useSelector(selectFilterSecurityLevel);
    const filterStartDate = useSelector(selectFilterStartDate);
    const filterEndDate = useSelector(selectFilterEndDate);

    // Local state
    const [selectedQuery, setSelectedQuery] = useState(null);
    const [showQueryDetail, setShowQueryDetail] = useState(false);

    // Fetch data on mount and when filters/pagination change
    useEffect(() => {
        dispatch(fetchQueries());
        dispatch(fetchStatistics());
    }, [
        dispatch,
        page,
        pageSize,
        sortColumn,
        sortDirection,
        filterDeviceId,
        filterUnitName,
        filterQueryType,
        filterStatus,
        filterSecurityLevel,
        filterStartDate,
        filterEndDate,
    ]);

    // Auto-refresh statistics every 30 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            dispatch(fetchStatistics());
        }, 30000);
        return () => clearInterval(interval);
    }, [dispatch]);

    const handleRefresh = () => {
        dispatch(fetchQueries());
        dispatch(fetchStatistics());
    };

    const handleDelete = (row) => {
        if (confirm(t('vneid.verificationQueries.deleteConfirm', { id: row.id }))) {
            dispatch(deleteQuery(row.id));
        }
    };

    const handleViewDetail = (row) => {
        setSelectedQuery(row);
        setShowQueryDetail(true);
    };

    const handleClearFilters = () => {
        dispatch(clearFilters());
    };

    // Query type options
    const queryTypeOptions = [
        { id: '', name: t('common.all') },
        { id: 'AUTHENTICATION', name: 'Authentication' },
        { id: 'VERIFICATION', name: 'Verification' },
        { id: 'VALIDATION', name: 'Validation' },
    ];

    // Status options
    const statusOptions = [
        { id: '', name: t('common.all') },
        { id: 'Pending', name: 'Pending' },
        { id: 'Success', name: 'Success' },
        { id: 'Failed', name: 'Failed' },
    ];

    // Security level options
    const securityLevelOptions = [
        { id: '', name: t('common.all') },
        { id: 'CAO', name: 'CAO (High)' },
        { id: 'TRUNG BINH', name: 'TRUNG BINH (Medium)' },
        { id: 'THAP', name: 'THAP (Low)' },
    ];

    // Table columns
    const columns = [
        { key: 'id', label: t('vneid.verificationQueries.table.id') },
        { key: 'deviceId', label: t('vneid.verificationQueries.table.deviceId') },
        { key: 'unitName', label: t('vneid.verificationQueries.table.unitName') },
        { key: 'queryType', label: t('vneid.verificationQueries.table.queryType') },
        {
            key: 'status',
            label: t('vneid.verificationQueries.table.status'),
            render: (value) => {
                const colorMap = {
                    Pending: 'warning',
                    Success: 'success',
                    Failed: 'error',
                };
                return <Chip label={value} color={colorMap[value] || 'default'} size="small" />;
            },
        },
        {
            key: 'responseTimeMs',
            label: t('vneid.verificationQueries.table.responseTime'),
            render: (value) => (value ? `${value} ms` : '-'),
        },
        { key: 'securityLevel', label: t('vneid.verificationQueries.table.securityLevel') },
        {
            key: 'createdOn',
            label: t('vneid.verificationQueries.table.createdOn'),
            render: (value) => new Date(value).toLocaleString(),
        },
    ];

    return (
        <Box>
            {/* Statistics Dashboard */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">{t('vneid.verificationQueries.statistics.title')}</Typography>
                    <IconButton onClick={handleRefresh} disabled={statisticsLoading}>
                        <RefreshIcon />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(4, 1fr)',
                        },
                        gap: 3,
                        width: '100%',
                    }}
                >
                    {/* Successful Queries Today */}
                    <Card sx={{ bgcolor: '#e8f5e9' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h4" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                                        {statistics.successfulQueriesToday || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('vneid.verificationQueries.statistics.successfulToday')}
                                    </Typography>
                                </Box>
                                <CheckCircleIcon sx={{ fontSize: 48, color: '#2e7d32', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Failed Queries Today */}
                    <Card sx={{ bgcolor: '#ffebee' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h4" sx={{ color: '#c62828', fontWeight: 'bold' }}>
                                        {statistics.failedQueriesToday || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('vneid.verificationQueries.statistics.failedToday')}
                                    </Typography>
                                </Box>
                                <ErrorIcon sx={{ fontSize: 48, color: '#c62828', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Average Response Time */}
                    <Card sx={{ bgcolor: '#e3f2fd' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h4" sx={{ color: '#1565c0', fontWeight: 'bold' }}>
                                        {statistics.averageResponseTimeMs || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('vneid.verificationQueries.statistics.avgResponseTime')} (ms)
                                    </Typography>
                                </Box>
                                <TimerIcon sx={{ fontSize: 48, color: '#1565c0', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Success Rate Last 24h */}
                    <Card sx={{ bgcolor: '#f3e5f5' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h4" sx={{ color: '#6a1b9a', fontWeight: 'bold' }}>
                                        {statistics.successRateLast24Hours || 0}%
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('vneid.verificationQueries.statistics.successRate24h')}
                                    </Typography>
                                </Box>
                                <TrendingUpIcon sx={{ fontSize: 48, color: '#6a1b9a', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Additional Statistics */}
                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                        label={`${t('vneid.verificationQueries.statistics.totalLast24h')}: ${statistics.totalQueriesLast24Hours || 0}`}
                        color="primary"
                        variant="outlined"
                    />
                    <Chip
                        label={`${t('vneid.verificationQueries.statistics.totalThisMonth')}: ${statistics.totalQueriesThisMonth || 0}`}
                        color="secondary"
                        variant="outlined"
                    />
                    <Chip
                        label={`${t('vneid.verificationQueries.statistics.pending')}: ${statistics.pendingQueries || 0}`}
                        color="warning"
                        variant="outlined"
                    />
                </Box>
            </Box>

            {/* Filters Section */}
            <Accordion defaultExpanded sx={{ mb: 3 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FilterListIcon />
                        <Typography variant="h6">{t('vneid.verificationQueries.filters.title')}</Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Input
                                label={t('vneid.verificationQueries.filters.deviceId')}
                                value={filterDeviceId}
                                onChange={(e) => dispatch(setFilterDeviceId(e.target.value))}
                                id="filterDeviceId"
                                name="filterDeviceId"
                                data-testid="verificationQueries-input-filterDeviceId"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Input
                                label={t('vneid.verificationQueries.filters.unitName')}
                                value={filterUnitName}
                                onChange={(e) => dispatch(setFilterUnitName(e.target.value))}
                                id="filterUnitName"
                                name="filterUnitName"
                                data-testid="verificationQueries-input-filterUnitName"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Select
                                label={t('vneid.verificationQueries.filters.queryType')}
                                value={filterQueryType}
                                onChange={(e) => dispatch(setFilterQueryType(e.target.value))}
                                options={queryTypeOptions}
                                id="filterQueryType"
                                name="filterQueryType"
                                data-testid="verificationQueries-select-filterQueryType"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Select
                                label={t('vneid.verificationQueries.filters.status')}
                                value={filterStatus}
                                onChange={(e) => dispatch(setFilterStatus(e.target.value))}
                                options={statusOptions}
                                id="filterStatus"
                                name="filterStatus"
                                data-testid="verificationQueries-select-filterStatus"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Select
                                label={t('vneid.verificationQueries.filters.securityLevel')}
                                value={filterSecurityLevel}
                                onChange={(e) => dispatch(setFilterSecurityLevel(e.target.value))}
                                options={securityLevelOptions}
                                id="filterSecurityLevel"
                                name="filterSecurityLevel"
                                data-testid="verificationQueries-select-filterSecurityLevel"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <DateRangePicker
                                label={t('vneid.verificationQueries.filters.dateRange')}
                                fromValue={filterStartDate || ''}
                                toValue={filterEndDate || ''}
                                onFromChange={(e) => dispatch(setFilterStartDate(e.target.value))}
                                onToChange={(e) => dispatch(setFilterEndDate(e.target.value))}
                                id="filterDateRange"
                                name="filterDateRange"
                                data-testid="verificationQueries-daterangepicker"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Button
                                variant="outlined"
                                onClick={handleClearFilters}
                                fullWidth
                                sx={{ height: '100%' }}
                                id="clearFilters"
                                name="clearFilters"
                                data-testid="verificationQueries-button-clearFilters"
                            >
                                {t('common.clearFilters')}
                            </Button>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* Queries Table */}
            <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    {t('vneid.verificationQueries.table.title')}
                </Typography>
                <Table
                    id="verification-queries-table"
                    columns={columns}
                    data={queries}
                    loading={loading}
                    pagination={true}
                    page={page}
                    pageSize={pageSize}
                    totalItems={total}
                    onPageChange={(newPage) => dispatch(setPage(newPage))}
                    onPageSizeChange={(newSize) => dispatch(setPageSize(newSize))}
                    sortable={true}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSortChange={(col, dir) => {
                        dispatch(setSortColumn(col));
                        dispatch(setSortDirection(dir));
                    }}
                    onDelete={handleDelete}
                    customActions={(row) => (
                        <Tooltip title={t('common.viewDetail')}>
                            <IconButton
                                size="small"
                                onClick={() => handleViewDetail(row)}
                                id={`viewDetail-${row.id}`}
                                name={`viewDetail-${row.id}`}
                                data-testid={`verification-queries-table-button-viewDetail-${row.id}`}
                            >
                                <VisibilityIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                />
            </Paper>

            {/* Query Detail Dialog */}
            <Dialog open={showQueryDetail} onClose={() => setShowQueryDetail(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {t('vneid.verificationQueries.detail.title')} #{selectedQuery?.id}
                </DialogTitle>
                <DialogContent>
                    {selectedQuery && (
                        <Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('vneid.verificationQueries.detail.deviceId')}
                                    </Typography>
                                    <Typography variant="body1">{selectedQuery.deviceId}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('vneid.verificationQueries.detail.unitName')}
                                    </Typography>
                                    <Typography variant="body1">{selectedQuery.unitName}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('vneid.verificationQueries.detail.queryType')}
                                    </Typography>
                                    <Typography variant="body1">{selectedQuery.queryType}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('vneid.verificationQueries.detail.status')}
                                    </Typography>
                                    <Chip
                                        label={selectedQuery.status}
                                        color={
                                            selectedQuery.status === 'Success' ? 'success' : selectedQuery.status === 'Failed' ? 'error' : 'warning'
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('vneid.verificationQueries.detail.responseTime')}
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedQuery.responseTimeMs ? `${selectedQuery.responseTimeMs} ms` : '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('vneid.verificationQueries.detail.securityLevel')}
                                    </Typography>
                                    <Typography variant="body1">{selectedQuery.securityLevel}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('vneid.verificationQueries.detail.requestData')}
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 1, mt: 1, maxHeight: 200, overflow: 'auto' }}>
                                        <pre style={{ margin: 0, fontSize: '0.875rem' }}>{selectedQuery.requestData || '-'}</pre>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('vneid.verificationQueries.detail.responseData')}
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 1, mt: 1, maxHeight: 200, overflow: 'auto' }}>
                                        <pre style={{ margin: 0, fontSize: '0.875rem' }}>{selectedQuery.responseData || '-'}</pre>
                                    </Paper>
                                </Grid>
                                {selectedQuery.errorMessage && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="error">
                                            {selectedQuery.errorMessage}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setShowQueryDetail(false)}
                        id="closeDetail"
                        name="closeDetail"
                        data-testid="verificationQueries-button-closeDetail"
                    >
                        {t('common.close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
